import React, { useEffect, useRef, useState } from 'react';
import type { NumericFormatProps } from 'react-number-format';
import { NumericFormat } from 'react-number-format';
import { useField } from 'formik';
import type { TextFieldProps } from '@material-ui/core';
import { TextField } from '@material-ui/core';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

type FormikNumericFormat = {
    isReadOnly?: boolean;
    isRequired?: boolean;
    align?: 'left' | 'right' | 'center';
} & NumericFormatProps<TextFieldProps>;

export const FormikNumericFormat = ({
    id,
    label,
    defaultValue,
    isReadOnly,
    isRequired,
    placeholder,
    align,
    ...rest
}: FormikNumericFormat) => {
    const classes = getFormFieldStyles();
    const inputRef = useRef<HTMLInputElement>(null);
    const [formikField, meta] = useField(id as string);
    const [localValue, setLocalValue] = useState(
        formikField.value ?? defaultValue,
    );

    const handleValueFormatting = (formattedValue?: string) => {
        if (inputRef && inputRef.current) {
            inputRef.current.value = formattedValue?.toString() ?? '';
        }
    };

    useEffect(() => {
        setLocalValue(formikField.value);
    }, [formikField.value]);

    return (
        <NumericFormat
            {...formikField}
            inputRef={inputRef}
            thousandSeparator={' '}
            decimalSeparator=","
            value={localValue}
            className={classNames({
                [classes.editableField]: !isReadOnly,
                [classes.readOnlyField]: isReadOnly,
            })}
            id={id}
            customInput={TextField}
            label={label}
            placeholder={placeholder}
            type="text"
            defaultValue={formikField?.value ?? defaultValue}
            fullWidth={true}
            multiline={true}
            required={isRequired}
            onValueChange={({ floatValue }) => {
                setLocalValue(floatValue);
                handleValueFormatting(floatValue?.toString());
            }}
            inputProps={{
                'aria-readonly': !!isReadOnly,
                style: align ? { textAlign: align } : undefined,
            }}
            disabled={!!isReadOnly}
            InputLabelProps={{
                shrink: true,
            }}
            helperText={meta.touched ? meta.error : ''}
            error={meta.touched && Boolean(meta.error)}
            {...rest}
        />
    );
};
